import { useEffect, useRef } from "react";

/**
 * Configuration options for the usePersistence hook.
 */
export interface UsePersistenceOptions<T> {
  /**
   * Unique key for storing/retrieving data from storage.
   */
  key: string;

  /**
   * The data to persist. When this changes, it will be saved after the debounce delay.
   */
  value: T;

  /**
   * Whether to persist the data. When false, any existing data will be removed.
   */
  shouldPersist: boolean;

  /**
   * Whether the component is currently restoring data from storage.
   * When true, prevents overwriting the storage with partial data during restoration.
   * @default false
   */
  isRestoring?: boolean;

  /**
   * Debounce delay in milliseconds before saving data to storage.
   * Helps reduce storage operations during rapid value changes.
   * @default 400
   */
  debounceMs?: number;

  /**
   * Custom serialization function. Defaults to JSON.stringify.
   */
  serialize?: (value: T) => string;

  /**
   * Storage save function. Called when data should be persisted.
   */
  onSave: (value: T) => void;

  /**
   * Storage remove function. Called when data should be removed.
   */
  onRemove: () => void;
}

/**
 * Generic hook for persisting data to storage with debouncing.
 *
 * This hook provides automatic persistence of data to storage (e.g., localStorage)
 * with debouncing to minimize storage operations. It also handles removal of data
 * when persistence is disabled.
 *
 * **Features:**
 * - Automatic debounced saving
 * - Restoration prevention during data loading
 * - Cleanup on unmount
 * - Customizable serialization
 * - Type-safe
 *
 * **Use Cases:**
 * - Form data auto-save
 * - User preferences
 * - Component state persistence
 * - "Remember me" functionality
 *
 * @template T The type of data being persisted
 * @param options Configuration options for persistence behavior
 *
 * @example
 * ```tsx
 * // Basic usage with localStorage
 * const { save, load, remove } = useLocalStorage('my-key');
 *
 * usePersistence({
 *   key: 'my-key',
 *   value: formData,
 *   shouldPersist: rememberMe,
 *   isRestoring: isLoading,
 *   debounceMs: 500,
 *   onSave: save,
 *   onRemove: remove
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With custom serialization
 * usePersistence({
 *   key: 'contact-details',
 *   value: contactData,
 *   shouldPersist: checked,
 *   serialize: (data) => btoa(JSON.stringify(data)), // Base64 encoding
 *   onSave: (data) => localStorage.setItem('key', serialize(data)),
 *   onRemove: () => localStorage.removeItem('key')
 * });
 * ```
 */
export function usePersistence<T>({
  key,
  value,
  shouldPersist,
  isRestoring = false,
  debounceMs = 400,
  serialize = JSON.stringify,
  onSave,
  onRemove
}: UsePersistenceOptions<T>): void {
  // Use refs to store the latest callbacks to avoid unnecessary effect re-runs
  const onSaveRef = useRef(onSave);
  const onRemoveRef = useRef(onRemove);

  // Update refs when callbacks change
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    onRemoveRef.current = onRemove;
  }, [onRemove]);

  useEffect(() => {
    // Skip save during restoration to avoid overwriting with partial data
    if (isRestoring) {
      return;
    }

    let timeoutId: number | undefined;

    if (shouldPersist) {
      // Debounce the save operation to avoid excessive storage writes
      timeoutId = window.setTimeout(() => {
        try {
          onSaveRef.current(value);
        } catch (error) {
          console.error(`[usePersistence] Error saving data for key "${key}":`, error);
        }
      }, debounceMs);
    } else {
      // Remove data immediately when persistence is disabled
      try {
        onRemoveRef.current();
      } catch (error) {
        console.error(`[usePersistence] Error removing data for key "${key}":`, error);
      }
    }

    // Cleanup: Clear timeout on unmount or when dependencies change
    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [key, value, shouldPersist, isRestoring, debounceMs, serialize]);
}
